import { useEffect, useState } from "react";
import { Thermometer, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PresencePersonVM } from "@/types/homey";
import { PresenceBadge } from "./PresenceBadge";
import { SensorBadge } from "./SensorBadge";
import { StatusPill } from "./StatusPill";

interface StatusBarProps {
  outsideTemp?: number | null;
  powerNowW?: number | null;
  securityAlerts?: number;
  /** Household members; rendered present-first as a compact avatar cluster. */
  presence?: PresencePersonVM[];
  className?: string;
}

/**
 * Slim status bar: live clock, full date, optional outside temperature + power badges, security pill.
 */
export function StatusBar({
  outsideTemp,
  powerNowW,
  securityAlerts = 0,
  presence,
  className,
}: StatusBarProps) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // 1s tick keeps the minute flip prompt; display self-corrects from new Date() each tick.
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const time = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const date = now.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div data-slot="status-bar" className={cn("flex items-center gap-3 text-sm", className)}>
      {/* Live clock */}
      <span
        role="timer"
        aria-label={`Current time: ${time}`}
        className="text-lg font-semibold tabular-nums"
      >
        {time}
      </span>

      <span className="text-muted-foreground">{date}</span>

      {/* Household presence cluster (present-first). Empty → render nothing. */}
      {presence && presence.length > 0 && (
        <div
          data-slot="presence-cluster"
          role="list"
          aria-label="Household presence"
          className="flex items-center -space-x-2"
        >
          {presence.map((person) => (
            <PresenceBadge key={person.id} role="listitem" person={person} />
          ))}
        </div>
      )}

      {/* Spacer */}
      <span className="flex-1" aria-hidden />

      {/* Optional outside temperature badge */}
      {typeof outsideTemp === "number" && (
        <SensorBadge
          icon={<Thermometer />}
          value={outsideTemp}
          unit="°"
          precision={1}
          domain="climate"
          aria-label={`Outside temperature: ${outsideTemp.toFixed(1)}°`}
        />
      )}

      {/* Optional power badge */}
      {typeof powerNowW === "number" && (
        <SensorBadge
          icon={<Zap />}
          value={powerNowW}
          unit="W"
          precision={0}
          domain="energy"
          aria-label={`Power: ${Math.round(powerNowW)} W`}
        />
      )}

      {/* Security status */}
      <StatusPill status={securityAlerts > 0 ? "alert" : "ok"}>
        {securityAlerts > 0
          ? `${securityAlerts} alert${securityAlerts === 1 ? "" : "s"}`
          : "Secure"}
      </StatusPill>
    </div>
  );
}
